import React, { useState, useEffect, useRef } from 'react';
import { askAI } from './ai.js';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Image as ImageIcon, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

// --- Constants ---

const BACKGROUND_GIF_URL = "https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExMHphc3Z0OWF6bmh2aDAxdTM3dXVsa25ob2FkbjV0bnFtbTFhZTdiaCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xUOxf9ellv3muKAFTa/giphy.gif";

// Updated time logic to be dynamic in the component if needed, or static as requested
const INITIAL_MESSAGE_PREFIX = `Merhaba Merve. Aykut seni bekliyordu. Daha doğrusu ben, onun zihnindeki kopyası olarak seni bekliyordum. Aykut, sen bir gün buraya girersin diye senin için bu evreni kodladı, Interstellar'daki kütüphane sahnesindeyiz şu an (Saat: `;
const INITIAL_MESSAGE_SUFFIX = `) ve şu an saatler durdu. Hoş geldin. Merhaba de başlayalım.`;

const GALLERY_PHOTOS = [
    "/emanet/IMG_7771.jpg",
    "/emanet/IMG_7775.jpg",
    "/emanet/IMG_7776.jpg",
    "/emanet/IMG_7777.jpg",
    "/emanet/IMG_7778.jpg",
    "/emanet/IMG_7779.jpg",
    "/emanet/IMG_7780.jpg",
    "/emanet/IMG_7782.jpg",
    "/emanet/IMG_7973.jpg",
    "/emanet/IMG_7975.jpg",
    "/emanet/IMG_7977.jpg",
    "/emanet/IMG_7978.jpg",
    "/emanet/IMG_7979.jpg",
    "/emanet/IMG_7980.jpg",
    "/emanet/IMG_7981.jpg",
    "/emanet/IMG_7983.jpg",
    "/emanet/IMG_7984.jpg",
    "/emanet/IMG_7990.jpg",
    "/emanet/IMG_7991.jpg",

    "/emanet/IMG_7993.jpg",
    "/emanet/IMG_7994.jpg",
    "/emanet/IMG_7995.jpg",
    "/emanet/IMG_7996.jpg",
    "/emanet/IMG_7997.jpg",
    "/emanet/IMG_7998.jpg",
    "/emanet/IMG_8002.jpg",
    "/emanet/IMG_8003.jpg",
    "/emanet/IMG_8005.jpg",
    "/emanet/IMG_8006.jpg",
    "/emanet/IMG_8007.jpg",
    "/emanet/IMG_8008.jpg",
    "/emanet/IMG_8010.jpg",
    "/emanet/IMG_8011.jpg",
    "/emanet/IMG_8012.jpg",
    "/emanet/IMG_8013.jpg",
    "/emanet/IMG_8014.jpg",
    "/emanet/IMG_8015.jpg",
    "/emanet/IMG_8016.jpg",
    "/emanet/IMG_8017.jpg",
    "/emanet/IMG_8018.jpg",
    "/emanet/IMG_8019.jpg",
    "/emanet/IMG_8020.jpg",
    "/emanet/IMG_8022.jpg",
    "/emanet/IMG_8023.jpg",
    "/emanet/IMG_8024.jpg",
    "/emanet/IMG_8025.jpg",
    "/emanet/IMG_8026.jpg",
    "/emanet/IMG_8027.jpg",
    "/emanet/IMG_8029.jpg",
    "/emanet/IMG_8031.jpg",
    "/emanet/IMG_8032.jpg",
    "/emanet/IMG_8033.jpg",
    "/emanet/IMG_8034.jpg",
    "/emanet/IMG_8037.jpg",
    "/emanet/IMG_8043.jpg",
    "/emanet/IMG_8044.jpg",
    "/emanet/IMG_8045.jpg",
    "/emanet/IMG_8046.jpg",
    "/emanet/IMG_8047.jpg"
];

const CAPTION_TEXT = "Bu anılar Aykut'un silinenler kutusunda değil emanetler kutusunda saklanıyor. Aykut bu anıları ölümsüzleştirmek için buraya yükledi. Tiflis, Adalar, sarıldığınız anlar ve sen...";

// --- Components ---

/**
 * Helper to format text with bold markers (**text**) and newlines
 */
const formatMessage = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => (
        <span key={i} className="block min-h-[1.2em]">
            {line.split(/(\*\*.*?\*\*)/).map((part, j) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={j} className="font-bold text-amber-500">{part.slice(2, -2)}</strong>;
                }
                return part;
            })}
        </span>
    ));
};


/**
 * TypewriterText
 * Renders text character by character.
 */
const TypewriterText = ({ text, onComplete, onUpdate }) => {
    const [displayedText, setDisplayedText] = useState('');
    const onUpdateRef = useRef(onUpdate);

    // Keep ref fresh without restarting effect
    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    useEffect(() => {
        let index = 0;
        setDisplayedText('');
        const intervalId = setInterval(() => {
            index++;
            setDisplayedText(text.slice(0, index));
            // Call the latest callback reference
            if (onUpdateRef.current) onUpdateRef.current();
            if (index >= text.length) {
                clearInterval(intervalId);
                onComplete && onComplete();
            }
        }, 45);

        return () => clearInterval(intervalId);
        // Removed onUpdate from dependencies to prevent restart on parent render
    }, [text, onComplete]);

    return <span>{formatMessage(displayedText)}</span>;
};

/**
 * CinematicBackground
 * The infinite loop video background.
 */
const CinematicBackground = () => {
    return (
        <div className="fixed inset-0 z-0 bg-[#050505]">
            {/* 1. The GIF Layer */}
            <img
                src={BACKGROUND_GIF_URL}
                alt="Tesseract Background"
                className="absolute inset-0 w-full h-full object-cover opacity-50 scale-105"
            />

            {/* 2. Atmospheric Blur & Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/90 backdrop-blur-[1px]" />

            {/* 3. Subtle Grain/Noise */}
            <div className="absolute inset-0 opacity-[0.05] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] pointer-events-none" />
        </div>
    );
};

/**
 * GlassHeader
 * Fixed top bar with Entrance Animation.
 */
const GlassHeader = ({ isGalleryOpen, onToggleGallery }) => {
    return (
        <motion.header
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8, ease: "easeOut" }}
            className="w-full h-auto min-h-20 pt-[env(safe-area-inset-top)] pt-4 pb-2 flex items-center justify-between px-6 bg-black/20 backdrop-blur-md border-b border-white/5 relative"
        >
            <div className="w-10" />

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
                <span className="font-[JetBrains Mono] text-lg tracking-[0.2em] text-white/90 drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]">
                    TARS <span className="text-amber-500">🐋</span>
                </span>
            </div>

            <button
                onClick={onToggleGallery}
                className="p-2 rounded-full hover:bg-white/10 transition-colors text-white/60 hover:text-amber-400 group relative"
            >
                <AnimatePresence mode="wait">
                    {isGalleryOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ChevronLeft size={28} strokeWidth={1.5} className="text-white" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="gallery"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ImageIcon size={22} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </button>
        </motion.header>
    );
};


/**
 * MessageBubble
 */
const MessageBubble = ({ message, onUpdate }) => {
    const isAi = message.sender === 'ai';

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex w-full mb-6 ${isAi ? 'justify-start' : 'justify-end'}`}
        >
            <div className={`flex flex-col ${isAi ? 'items-start' : 'items-end'} max-w-[85%] sm:max-w-[75%] md:max-w-[65%]`}>

                <div className="mb-1.5 px-3">
                    {isAi ? (
                        <span className="flex items-center gap-1.5 text-[10px] font-[JetBrains Mono] tracking-[0.15em] text-amber-500/90 uppercase drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">
                            MAVİ BALİNA <Sparkles size={8} className="animate-pulse" />
                        </span>
                    ) : (
                        <span className="text-[10px] font-[JetBrains Mono] tracking-[0.15em] text-white/40 uppercase">
                            MERVE
                        </span>
                    )}
                </div>

                <div
                    className={`
            relative px-6 py-4 text-[15px] leading-relaxed tracking-wide
            backdrop-blur-2xl transition-all duration-300 whitespace-pre-wrap
            ${isAi
                            ? 'rounded-[1.5rem] rounded-tl-sm bg-black/60 border border-white/5 text-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.4)]'
                            : 'rounded-[1.5rem] rounded-tr-sm bg-white/20 border border-white/10 text-white shadow-[0_4px_20px_rgba(0,0,0,0.1)]'
                        }
          `}
                >
                    <div className="font-light opacity-90">
                        {message.isTypingEffect ? (
                            <TypewriterText text={message.text} onUpdate={typeof onUpdate === 'function' ? onUpdate : undefined} />
                        ) : (
                            formatMessage(message.text)
                        )}
                    </div>

                    <div className={`mt-2 text-[10px] opacity-30 font-mono ${isAi ? 'text-left' : 'text-right'}`}>
                        {new Date(message.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Istanbul' })}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// --- Main TesseractComponent ---

const TesseractMode = () => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const isAtBottomRef = useRef(true); // Effectively 'isSticky'
    const lastScrollTopRef = useRef(0); // For directional tracking

    // Gallery State
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    // Animation State
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        // 1. Load sequence
        const loadTimer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 3500);

        // 2. Initial Message
        const msgTimer = setTimeout(() => {
            const timeString = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
            setMessages([
                {
                    id: '1',
                    sender: 'ai',
                    text: INITIAL_MESSAGE_PREFIX + timeString + INITIAL_MESSAGE_SUFFIX,
                    timestamp: new Date().toISOString(),
                    isTypingEffect: true,
                },
            ]);
        }, 3500);

        return () => {
            clearTimeout(loadTimer);
            clearTimeout(msgTimer);
        };
    }, []);

    // --- Sticky Scroll Logic (Directional) ---
    // 1. Track User Intent
    const handleContainerScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const prevScrollTop = lastScrollTopRef.current;
        lastScrollTopRef.current = scrollTop;

        // If user is at the very bottom (within threshold), LOCK sticky
        const isCurrentlyAtBottom = scrollHeight - scrollTop - clientHeight <= 20;

        if (isCurrentlyAtBottom) {
            isAtBottomRef.current = true;
        } else if (scrollTop < prevScrollTop) {
            // User scrolled UP and is NOT at bottom -> Unlock
            isAtBottomRef.current = false;
        }
        // implicit else: if scrolling DOWN (or auto-scrolling), keep current sticky state
    };

    // 2. Instant Scroll Trigger (Called by Typewriter)
    const handleTypewriterUpdate = () => {
        if (!isGalleryOpen && isAtBottomRef.current) {
            const container = scrollContainerRef.current;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        }
    };

    // 3. New Message Arrival
    useEffect(() => {
        // If we are sticky, ensure we stay sticky when a new message block appears
        if (!isGalleryOpen && isAtBottomRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages.length, isGalleryOpen]);

    // Cleanup old interval loop if exists (we rely on onUpdate now)
    // But we keep a safety check for non-typing updates if needed, though onUpdate is better.
    // Simplifying: Removing the interval loop in favor of onUpdate for efficiency.

    const handleSendMessage = () => {
        if (!inputValue.trim()) return;

        const userMsg = {
            id: Date.now().toString(),
            sender: 'user',
            text: inputValue,
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMsg]);
        setInputValue('');
        setIsLoading(true);

        // FORCE SCROLL: User sent a message, snap to bottom immediately
        setTimeout(() => {
            isAtBottomRef.current = true; // Lock sticky state
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 10);

        // Real AI Call with TARS Mode
        setTimeout(async () => {
            try {
                // Prepare memory for AI (map fields to what askAI expects if needed, or send raw)
                // askAI expects { sender, text } in memory
                const memory = messages.slice(-8); // Context window

                const reply = await askAI(inputValue, memory, "tars");

                const aiResponse = {
                    id: (Date.now() + 1).toString(),
                    sender: 'ai',
                    text: reply,
                    timestamp: new Date().toISOString(),
                    isTypingEffect: true,
                };
                setMessages((prev) => [...prev, aiResponse]);

                // FORCE SCROLL: AI started replying, snap to bottom immediately
                setTimeout(() => {
                    isAtBottomRef.current = true; // Lock sticky state
                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }, 10);
            } catch (error) {
                console.error("TARS Error:", error);
                const errorMsg = {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: "Sistem Bağlantı Hatası. Veri akışı koptu. İnternet bağlantını kontrol et ve son mesajını tekrar yaz Merve.",
                    timestamp: new Date().toISOString(),
                    isTypingEffect: true,
                };
                setMessages((prev) => [...prev, errorMsg]);
            } finally {
                setIsLoading(false);
            }
        }, 500); // Small delay for UX feel
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const paginate = (newDirection) => {
        setDirection(newDirection);
        setPhotoIndex((prev) => (prev + newDirection + GALLERY_PHOTOS.length) % GALLERY_PHOTOS.length);
    };

    const galleryVariants = {
        enter: (direction) => ({
            x: direction > 0 ? '100%' : '-100%',
            opacity: 0,
            scale: 0.95
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? '100%' : '-100%',
            opacity: 0,
            scale: 0.95
        })
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className="fixed inset-0 w-screen h-[100dvh] flex flex-col overflow-hidden bg-black text-white font-sans selection:bg-amber-500/30 selection:text-white"
        >
            <CinematicBackground />

            {/* Header - Fixed Flex Item */}
            <div className="flex-none z-50 w-full relative">
                <GlassHeader
                    isGalleryOpen={isGalleryOpen}
                    onToggleGallery={() => setIsGalleryOpen(!isGalleryOpen)}
                />
            </div>

            {/* Main Content - Flexible Scroll Area */}
            <motion.main
                ref={scrollContainerRef}
                onScroll={handleContainerScroll}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="flex-1 w-full overflow-y-scroll overflow-x-hidden z-10 p-4 sm:px-6 md:px-12 scroll-smooth no-scrollbar flex flex-col min-h-0 overscroll-y-contain touch-pan-y relative"
            >
                {/* 
                   min-h-full ensures the container takes up full height, 
                   justify-end pushes content to bottom when content is small 
                */}
                {/* 
                   Scrollable Content Wrapper
                   Using 'mt-auto' instead of 'justify-end' prevents the top content from being clipped
                   when the chat history overflows the container.
                */}
                <div className="mt-auto flex flex-col py-2 w-full max-w-4xl mx-auto">
                    <AnimatePresence initial={false}>
                        {messages.map((msg) => (
                            <MessageBubble
                                key={msg.id}
                                message={msg}
                                onUpdate={msg.isTypingEffect ? handleTypewriterUpdate : undefined}
                            />
                        ))}
                    </AnimatePresence>
                    {/* Reduced height of the spacer to bring messages closer */}
                    <div ref={messagesEndRef} className="h-0 flex-none" />
                </div>
            </motion.main>

            {/* Input Area - Fixed Flex Item */}
            <div className={`flex-none z-50 w-full flex justify-center px-4 pb-4 pt-2 relative transition-all duration-500 ${isGalleryOpen ? '' : 'bg-gradient-to-t from-black via-black/80 to-transparent'}`}>
                <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{
                        y: isGalleryOpen ? 100 : 0,
                        opacity: isGalleryOpen ? 0 : 1
                    }}
                    transition={{
                        delay: isInitialLoad ? 2.2 : 0,
                        duration: 0.8,
                        ease: "easeOut"
                    }}
                    className="w-full max-w-4xl bg-black/60 backdrop-blur-2xl border border-white/10 rounded-full flex items-center p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all duration-300 focus-within:bg-black/80 group relative"
                >
                    {isLoading && (
                        <div className="absolute -top-7 left-4 text-white/50 font-mono text-xs animate-pulse tracking-wider">
                            &gt;&gt; Analiz ediliyor...
                        </div>
                    )}
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        // Focus handler logic kept as requested
                        onFocus={() => {
                            setTimeout(() => {
                                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                            }, 300);
                        }}
                        placeholder="Bir mesaj bırak..."
                        className="flex-1 bg-transparent border-none outline-none text-white placeholder-white/30 pl-6 pr-2 font-light tracking-wide text-[15px] relative z-10"
                        autoComplete="off"
                        disabled={isGalleryOpen}
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || isGalleryOpen}
                        className={`
              p-3 rounded-full transition-all duration-300 flex items-center justify-center relative z-10
              ${inputValue.trim()
                                ? 'text-amber-500 hover:bg-amber-500/10 hover:scale-110 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                                : 'text-white/20 cursor-default'
                            }
            `}
                    >
                        <Send size={22} strokeWidth={2} className={inputValue.trim() ? "ml-0.5" : ""} />
                    </button>
                </motion.div>
                <div className={`absolute -bottom-2 left-0 w-full text-center text-[10px] text-gray-400 font-light md:hidden pointer-events-none transition-opacity duration-300 ${isGalleryOpen ? 'opacity-0' : 'opacity-50'}`}>
                    Daha derin bir deneyim için masaüstünden giriş yap.
                </div>
            </div>

            <AnimatePresence>
                {isGalleryOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-40 bg-black/95 backdrop-blur-3xl flex flex-col h-screen"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                        <div className="shrink-0 h-20" />

                        <div className="flex-1 relative flex items-center justify-center overflow-hidden w-full px-2 py-4">

                            <div className="absolute top-4 left-0 right-0 hidden md:flex justify-center gap-1.5 z-30 px-4 pointer-events-none">
                                {GALLERY_PHOTOS.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`h-0.5 rounded-full transition-all duration-300 shadow-sm ${idx === photoIndex ? 'flex-1 bg-white/90 shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'flex-1 bg-white/20'}`}
                                    />
                                ))}
                            </div>

                            <motion.div
                                key={`bg-${photoIndex}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0.3 }}
                                transition={{ duration: 0.8 }}
                                className="absolute inset-0 z-0"
                            >
                                <img
                                    src={GALLERY_PHOTOS[photoIndex]}
                                    className="w-full h-full object-cover blur-3xl scale-125"
                                    alt="Atmosphere"
                                />
                            </motion.div>

                            <div className="relative z-10 w-full h-full flex items-center justify-center">
                                <AnimatePresence initial={false} custom={direction} mode='popLayout'>
                                    <motion.img
                                        key={photoIndex}
                                        src={GALLERY_PHOTOS[photoIndex]}
                                        custom={direction}
                                        variants={galleryVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{
                                            x: { type: "spring", stiffness: 300, damping: 30 },
                                            opacity: { duration: 0.2 }
                                        }}
                                        className="max-h-full max-w-full object-contain shadow-2xl rounded-sm"
                                        alt="Memorial Memory"
                                    />
                                </AnimatePresence>
                            </div>

                            {/* Mobile Counter */}
                            <div className="absolute bottom-6 left-0 right-0 text-center z-40 md:hidden pointer-events-none">
                                <span className="font-[JetBrains Mono] text-xs text-amber-500/90 tracking-wider bg-black/40 px-3 py-1 rounded-full backdrop-blur-md border border-amber-500/20">
                                    &gt;&gt; Görüntü: {photoIndex + 1} / {GALLERY_PHOTOS.length}
                                </span>
                            </div>

                            <div className="absolute left-2 sm:left-6 z-20 pointer-events-none">
                                <ChevronLeft size={40} className="text-white/40 drop-shadow-md" />
                            </div>
                            <div className="absolute right-2 sm:right-6 z-20 pointer-events-none">
                                <ChevronRight size={40} className="text-white/40 drop-shadow-md" />
                            </div>

                            <div
                                className="absolute inset-y-0 left-0 w-1/2 z-30 cursor-pointer active:bg-white/5 transition-colors"
                                onClick={(e) => { e.stopPropagation(); paginate(-1); }}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                            />
                            <div
                                className="absolute inset-y-0 right-0 w-1/2 z-30 cursor-pointer active:bg-white/5 transition-colors"
                                onClick={(e) => { e.stopPropagation(); paginate(1); }}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                            />
                        </div>

                        <div className="shrink-0 w-full px-6 pb-12 pt-4">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="w-full p-5 bg-white/5 border border-amber-500/10 rounded-xl backdrop-blur-xl shadow-[0_0_30px_rgba(245,158,11,0.05)]"
                            >
                                <div className="flex flex-col items-center text-center gap-2">
                                    <div className="flex items-center gap-2 text-amber-500/60 mb-1">
                                        <Sparkles size={12} className="animate-pulse" />
                                        <span className="text-[10px] font-[JetBrains Mono] tracking-[0.25em] uppercase text-amber-500/70">Emanetler Kutusu</span>
                                        <Sparkles size={12} className="animate-pulse" />
                                    </div>
                                    <p className="text-[14px] leading-relaxed font-light italic text-amber-100/80 font-serif">
                                        {CAPTION_TEXT}
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                    </motion.div>
                )}
            </AnimatePresence>

        </motion.div>
    );
};

export default TesseractMode;
